import { isFulfilled } from "@reduxjs/toolkit";
import { typeOrder } from "constants/AppConstants";
import { useLoader } from "contexts/LoaderContext";
import { useFormTemplate, useGroupedItems } from "hooks/commonHandlers";
import { useAgentFormSync } from "hooks/useAgentFormSync";
import { useDeviceType } from "hooks/useDeviceType";
import TenantLayout from "pages/Layout/Tenant/TenantLayout";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FormProvider, SubmitHandler, useForm, useWatch } from "react-hook-form";
import { AGENT_SUCCESS_PATH } from "routes/agentRoutes";
import { formatTenantList } from "services/TenantSubmitHandler";
import { useAppDispatch, useAppSelector } from "store/configureStore";
import { setError } from "store/formTemplate/formTemplateSlice";
import { fetchFormTemplates } from "store/formTemplate/formTemplateThunks";
import { FormJsonSection, PetDataFormat } from "store/formTemplate/formTemplateTypes";
import { getTenantPetApplication, submitTenantResponseThunk } from "store/tenant/thunks";
import { SubmitAgentRequestPayload, TenantPetsData, TenantPetsDocument } from "store/tenant/types";
import { FormTemplateItem, hasFieldsList } from "types/formTemplate";
import { getPetInputByUuid, getProfileTitle, isQldRegion } from "utils/petFormHelpers";
import { getAnimalCardProfile, renderTemplates } from "utils/renderTemplates";
import { IconName } from "../../components/AnimalCard";
import AnimalProfile from "../../components/AnimalProfile/AnimalProfile";
import ApplicationSummary from "../../components/ApplicationSummary";
import Button from "../../components/UI/Button";
import ErrorAlert from "../../components/UI/ErrorAlert";
import Modal from "../../components/UI/Modal";
import { secureLocalStorage } from "../../utils/secureStorage";
import "./agent_style.scss";
import AgentLandLord from "./AgentLandLord";
import ProfileModal from "./ProfileModal";
import SupportDocument from "./SupportDocument";

type DynamicItem = {
  type: IconName;
  uid: number;
  pet_id?: string | null;
};

type FormValues = {
  username: string;
  role: string;
  [key: string]: any; // Allow dynamic keys for animal types
};

const Agent: React.FC = () => {
  // Modal form for profile view
  const modalForm = useForm<FormValues>({});
  // Remove saveType state, will pass as param
  const [showSaveSuccessModal, setShowSaveSuccessModal] = useState(false);
  // Redux selectors
  const dispatch = useAppDispatch();
  const applicationReceivedOn = useAppSelector(
    (state) => state.Tenant.applicationReceivedOn
  );
  const dayLeftToAutoComplete = useAppSelector(
    (state) => state.Tenant.dayLeftToAutoComplete
  );
  const error = useAppSelector((state) => state.Tenant.error);
  const primaryTenants = useAppSelector((state) => state.Tenant.primaryTenants);
  const tenantPropertyAddress = useAppSelector(
    (state) => state.Tenant.tenantPropertyAddress
  );
  const stageOnePetsData = useAppSelector((state) => state.Tenant.tenantPets);
  const stageOnePetsDocumentData = useAppSelector(
    (state) => state.Tenant.tenantPetsDocuments
  );
  const rawFormTemplate = useAppSelector(
    (state) => state.formTemplate.templates
  );
  const applicationStatus = useAppSelector((state) => state.Tenant.applicationStatus);

    const {
      agentApprovedApplications,
      agentApprovedlandlord,
      agentApprovedPetsDetails,
    } = useAppSelector((state) => ({
      agentApprovedResponse: state.Tenant.agentApprovedResponse,
      agentApprovedApplications: state.Tenant.agentApprovedApplications,
      agentApprovedlandlord: state.Tenant.agentApprovedlandlord,
      agentApprovedPetsDetails: state.Tenant.agentApprovedPetsDetails,
    }));



  // Local state
  const [nextUid, setNextUid] = useState(1);
  const [isAllApproved, setIsAllApproved] = useState(false);
  const [activeStage, setActiveStage] = useState(1);
  const [mobileActiveStage, setMobileActiveStage] = useState(1);
  const [items, setItems] = useState<DynamicItem[]>([]);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedPetData, setSelectedPetData] = useState<any>(null);
  const [selectedPetDocuments, setSelectedPetDocuments] = useState<any[]>([]);
  const [selectedType, setSelectedType] = useState<string>("");
  const [selectedIdx, setSelectedIdx] = useState<number>(0);
  const [landLordStage, setLandLordStage] = useState<number>(1);
  const [bottomBarHeight, setBottomBarHeight] = useState(0);
  const [showAgentDecisionModal, setShowAgentDecisionModal] = useState(false);
  const [openProfileKey, setOpenProfileKey] = useState<string | null>(null);
  const [agentDecisionType, setAgentDecisionType] = useState<string>(isQldRegion() ? "i_will_complete_then_request_sign" : "");
  const profileRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Form methods
  const methods = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: isQldRegion() ? { agent_decision_type: "i_will_complete_then_request_sign" } : {},
  });
  const { register, handleSubmit, getValues, watch, setValue, reset, formState, unregister, trigger } = methods;
  const { isValid } = formState;


  // Routing variables
  const urlParts = window.location.pathname.split("/");
  const agentId = urlParts?.[2];
  const agentLink = urlParts?.[4];

  // Loader context
  const { showLoader, hideLoader } = useLoader();
  const deviceType = useDeviceType();

  // Hooks
  const formTemplate = useFormTemplate(rawFormTemplate);
  const groupedItems = useGroupedItems(items);
  const agentFomTemplate = useFormTemplate(rawFormTemplate);

  // Effects


    const bottomBarRef = useRef<HTMLDivElement>(null);    
    
    useEffect(() => {
      if (!bottomBarRef.current) return;

      const updateHeight = () => {
          setBottomBarHeight(((bottomBarRef.current?.offsetHeight ?? 0)) || 0);
      };

      updateHeight();
      window.addEventListener('resize', updateHeight);

      return () => window.removeEventListener('resize', updateHeight);
    }, []);
  

    useEffect(() => {
      // Prevent API call on success page
        showLoader();
        dispatch(getTenantPetApplication({ agentId, agentLink }))
          .then(() => {
            const region = localStorage.getItem('pet_app_region') || undefined;
            return dispatch(fetchFormTemplates({agentId, region}));
          })
          .finally(hideLoader);
    }, [dispatch, showLoader, hideLoader]);

  useEffect(() => {
    if (stageOnePetsData && stageOnePetsData.length > 0) {
      // Order by pet_type using typeOrder array
      let uidCounter = 0;
      let lastType: string | null = null;
      const initialItems = typeOrder.flatMap((type) =>
        stageOnePetsData
          .filter((pet) => pet.pet_type === type)
          .map((pet, idx, arr) => {
        // Reset uidCounter if pet_type changes
        if (lastType !== type) {
          uidCounter = 0;
          lastType = type;
        }
        const item = {
          type: pet.pet_type as IconName,
          uid: uidCounter,
          pet_id: pet.id,
        };
        uidCounter++;
        return item;
          })
      );
      setItems(initialItems);
      setNextUid(initialItems.length + 1);

      if (initialItems.length > 0) {
        // setOpenProfileKey(`${initialItems[0].type}-${initialItems[0].uid}`);
      }
    }
  }, [stageOnePetsData]);

    /**
   * Added to reset conditions if "I do not consent" is selected, faced below issue while testing
    If the user first selects "I consent" and "no conditions", but then changes to "I do not consent", 
    the previously selected "I consent" and "no conditions" values should be reset to null.
   */
  // Extract watched values outside of useEffect for stable dependencies
  // Use useWatch for more granular control and avoid infinite loop
  

  const consentValues = useWatch({
    control: methods.control,
    name: items.map(item => `${item.type}.agent_consent.${item.uid}`)
  });
  const agentConditions = useWatch({
    control: methods.control,
    name: items.map(item => `${item.type}.agent_consent_conditions.${item.uid}`)
  });

  useEffect(() => {
    let cleared = false;

    // Idempotent clear helpers: only write (and only mark `cleared`) when the field's value
    // ACTUALLY needs to change. Without this guard the effect re-applied every setValue and
    // re-scheduled trigger() on every run, and because trigger() causes a re-render that
    // re-runs this effect, it became an infinite loop (consent stuck on a value while the
    // page re-rendered forever). Guarding on the current value lets the effect settle.
    const clearBool = (name: string) => {
      if (getValues(name)) {
        setValue(name, false, { shouldValidate: false });
        cleared = true;
      }
    };
    const clearText = (name: string) => {
      const current = getValues(name);
      if (current !== "" && current !== undefined && current !== null) {
        setValue(name, "", { shouldValidate: false });
        cleared = true;
      }
    };
    const clearArray = (name: string) => {
      const current = getValues(name);
      if (Array.isArray(current) && current.length > 0) {
        setValue(name, [], { shouldValidate: false });
        cleared = true;
      }
    };

    items.forEach((item, idx) => {
      const consentValue = consentValues?.[idx];
      const agentCondition = agentConditions?.[idx];
      const base = `${item.type}`;
      const u = item.uid;

      // When "I do not consent" is selected, clear all consent-related fields
      if (consentValue === "i_don_not_consent") {
        clearText(`${base}.agent_consent_conditions.${u}`);
        clearBool(`${base}.other_reasonable_conditions.${u}`);
        clearText(`${base}.condisions_details.${u}`);
        clearBool(`${base}.clause_57.${u}`);
        clearBool(`${base}.clause_58.${u}`);
        clearBool(`${base}.clause_59.${u}`);
        // QLD: the "Are there any conditions?" answer + the Condition/Reason textareas appear
        // under "I consent" -> "Yes". Clearing the controlling answer makes their showIf fail
        // (so they hide), and clearing the textareas removes the values that drove the
        // conditional `required` rule. No-ops on NSW (these fields don't exist there).
        if (isQldRegion()) {
          clearText(`${base}.pet_keeping_conditions.${u}`);
          clearText(`${base}.pet_condition.${u}`);
          clearText(`${base}.pet_condition_reason.${u}`);
        }
      }

      // When "I consent" is selected, clear all decline-related fields
      if (consentValue === "i_consent") {
        clearBool(`${base}.unreasonable_number_of_animala.${u}`);
        clearBool(`${base}.fencing.${u}`);
        clearBool(`${base}.open_space.${u}`);
        clearBool(`${base}.welfare.${u}`);
        clearBool(`${base}.damage.${u}`);
        clearBool(`${base}.other_laws.${u}`);
        clearBool(`${base}.landlord_lives_at_property.${u}`);
        clearBool(`${base}.tenant_did_not_agree.${u}`);
        // Clear the explanation textarea
        clearText(`${base}.selected_reason_for_apply.${u}`);
        // Clear the "I have read" checkbox for decline
        clearBool(`${base}.conditions_tenants.${u}`);
        // QLD: the decline "reason" textarea is shown via a refusal checkbox (e.g. "another
        // ground"), not directly by agent_consent — so clearing the QLD refusal checkboxes is
        // what makes that section disappear when switching back to consent. Also removes their
        // group "at least one refusal reason" pressure. No-ops on NSW (fields don't exist there).
        if (isQldRegion()) {
          clearBool(`${base}.qld_unreasonable_number.${u}`);
          clearBool(`${base}.qld_unsuitable_premises.${u}`);
          clearBool(`${base}.qld_likely_damage.${u}`);
          clearBool(`${base}.qld_health_safety_risk.${u}`);
          clearBool(`${base}.qld_contravene_law.${u}`);
          clearBool(`${base}.qld_body_corporate_bylaw.${u}`);
          clearBool(`${base}.qld_tenant_not_agreed_conditions.${u}`);
          clearBool(`${base}.qld_not_a_pet.${u}`);
          clearBool(`${base}.qld_licence_condition.${u}`);
          clearBool(`${base}.qld_another_ground.${u}`);
          // The decline grounds also persist in the `agent_decline_reasons` array (populated on
          // load) and the explanation/comments fields, which the per-checkbox clears don't touch.
          // Clearing them removes the leftover state that keeps the decline reason section/textarea
          // visible after switching back to consent.
          clearArray(`${base}.agent_decline_reasons.${u}`);
          clearText(`${base}.agent_decline_explanation.${u}`);
          clearText(`${base}.agent_comments.${u}`);
        }
      }

      // When "No conditions" is selected, clear all clause fields and the controlling checkbox
      if (agentCondition === "i_consent_no_conditions") {
        clearBool(`${base}.clause_57.${u}`);
        clearBool(`${base}.clause_58.${u}`);
        clearBool(`${base}.clause_59.${u}`);
        clearBool(`${base}.other_reasonable_conditions.${u}`);
        clearText(`${base}.condisions_details.${u}`);
      }
    });
    // Deferred re-validation: after clearing fields with shouldValidate:false,
    // isValid becomes stale. We only trigger the consent fields (which always
    // have a value at this point) to avoid showing validation messages on
    // untouched fields. Only runs when something was actually cleared, so it no
    // longer self-triggers a render loop.
    if (cleared) {
      const consentFieldNames = items.map(item => `${item.type}.agent_consent.${item.uid}`);
      setTimeout(() => trigger(consentFieldNames), 0);
    }
  }, [items, consentValues, agentConditions, setValue, getValues, trigger]);


  /**
   * Already on success page, redirect to success page if applicationStatus changes to agent_approved
   * This is to handle the case where the user is on the agent page and the applicationStatus changes to agent_approved
   * due to some other action (e.g. another agent approves the application)
   * We want to redirect the user to the success page in this case
   */
  useEffect(() => {
    if (applicationStatus === "agent_approved" || applicationStatus === "landlord_approved" || applicationStatus === "landlord_declined" || applicationStatus === "auto_approved" || applicationStatus === "agent_partial_approved") {
      window.location.replace(AGENT_SUCCESS_PATH); // replace() = full reload AND removes the submitted form from history so Back can't return to it (or restore it from bfcache)
    }

    console.log("Application status changed:", applicationStatus);
  }, [applicationStatus]);

  // QLD: region is resolved asynchronously (from the API), so the agent_decision_type
  // default set at init can be missed. Its `required` rule — registered when the NSW
  // decision-type section briefly renders during that race — then keeps `isValid` false
  // and blocks Submit, even though the radio is hidden for QLD. Once region is known,
  // force the QLD decision type so the hidden field's rule is satisfied. Also keeps the
  // Submit button label/icon correct. NSW is untouched (guarded by isQldRegion()).
  useEffect(() => {
    if (!isQldRegion()) return;
    setAgentDecisionType("i_will_complete_then_request_sign");
    if (getValues("agent_decision_type") !== "i_will_complete_then_request_sign") {
      setValue("agent_decision_type", "i_will_complete_then_request_sign", { shouldValidate: true });
    }
  }, [rawFormTemplate, applicationStatus, getValues, setValue]);

  /**
   * Sync form with agent approved data from store
   * This will populate the form fields with existing data when the component mounts or when the data changes
   */
  useAgentFormSync({
    items,
    agentApprovedlandlord,
    agentApprovedApplications,
    agentApprovedPetsDetails: agentApprovedPetsDetails ?? [],
    setValue,
    watch,
    reset,
    control: methods.control,
    skipConsentSync: true // Agent page handles consent clearing inline (lines 190-243)
  });

 

  const handleAddCard = useCallback((type: IconName) => {
    setItems(prev => [...prev, { type, uid: nextUid }]);
    setNextUid(prev => prev + 1);
  }, [nextUid]);

  const handleUploadPetDocuments = React.useCallback((uploadedFiles: any) => {},
  []);

 const renderAnimalCards = useCallback((animalType: string, animalIndex: number) => {
    const profiles = getAnimalCardProfile(formTemplate, animalType)?.[0] || [];
    if (profiles?.fieldsList?.length > 0) {
      return renderTemplates(
        profiles.fieldsList,
        { register, getValues, watch, handleAddCard, handleUploadPetDocuments, unregister },
        animalIndex,
        animalType,
        stageOnePetsData,
        stageOnePetsDocumentData,
        "agent"
      );
    }
    return null;
  }, [formTemplate, register, getValues, watch, handleAddCard, handleUploadPetDocuments, stageOnePetsData, stageOnePetsDocumentData, unregister]);


    /**
   * Handle bulk approve all pets - toggle between approve all and reset all
   * If not all approved, set all to approved, else reset all to empty
   * Also update button text accordingly
   */
  const handleBulkApproveAll = useCallback(() => {
    if(isAllApproved) {
      // If currently all approved, reset/clear all values
      items.forEach(item => {
        setValue(`${item.type}.agent_consent.${item.uid}`, "");
        setValue(`${item.type}.agent_consent_conditions.${item.uid}`, "");
        unregister(`${item.type}.agent_consent_conditions.${item.uid}`);
        unregister(`${item.type}.agent_consent.${item.uid}`);

        // QLD: also clear the separate "Are there any conditions?" Yes/No answer
        if (isQldRegion()) {
          setValue(`${item.type}.pet_keeping_conditions.${item.uid}`, "");
          unregister(`${item.type}.pet_keeping_conditions.${item.uid}`);
        }
      });
    } else {
      // If not all approved, set all to approved
      items.forEach(item => {
        setValue(`${item.type}.agent_consent.${item.uid}`, "i_consent");
        setValue(`${item.type}.agent_consent_conditions.${item.uid}`, "i_consent_no_conditions");

        // QLD: the "Are there any conditions?" answer is a separate Yes/No radio
        // (pet_keeping_conditions); "approved without conditions" => select "No".
        if (isQldRegion()) {
          setValue(`${item.type}.pet_keeping_conditions.${item.uid}`, "no");
        }
      });
    }
    setIsAllApproved(!isAllApproved);
    const btn = document.querySelector("#bulk-approve-button");
    if (btn) {
      btn.innerHTML = !isAllApproved
      ? `<svg fill=none height=24 viewBox="0 0 24 24"width=24 xmlns=http://www.w3.org/2000/svg><path d="M10.709 2.53613C11.0399 2.29975 11.5 2.53667 11.5 2.94336V5.5H12C16.1439 5.5 19.5 8.85614 19.5 13C19.5 17.1439 16.1439 20.5 12 20.5C8.1734 20.5 5.0178 17.6377 4.55762 13.9365C4.53173 13.7283 4.70487 13.5 5 13.5C5.2592 13.5 5.53026 13.7229 5.58984 14.0781C6.10479 17.1521 8.78066 19.5 12 19.5C15.5861 19.5 18.5 16.5861 18.5 13C18.5 9.41386 15.5861 6.5 12 6.5H11.5V9.05664C11.5 9.46333 11.0399 9.70025 10.709 9.46387L6.42969 6.40723C6.15051 6.20782 6.15051 5.79218 6.42969 5.59277L10.709 2.53613Z"fill=white stroke=white /></svg>Undo Bulk Approval`
      : `<svg fill=none height=24 viewBox="0 0 24 24"width=24 xmlns=http://www.w3.org/2000/svg><circle cx=12 cy=12 r=9 stroke=white stroke-width=2 /><path clip-rule=evenodd d="M16.3683 9.13327C16.5445 9.31036 16.5438 9.59679 16.3667 9.77303L10.4575 15.654C10.281 15.8296 9.99577 15.8296 9.81928 15.654L7.13327 12.9808C6.95618 12.8046 6.95549 12.5181 7.13173 12.3411C7.30798 12.164 7.59441 12.1633 7.77149 12.3395L10.1384 14.6951L15.7285 9.13173C15.9056 8.95549 16.192 8.95618 16.3683 9.13327Z"fill=white fill-rule=evenodd stroke=white stroke-linecap=round stroke-linejoin=round /></svg>  Mark All Approved`;
    }
  }, [items, isAllApproved, setValue]);
 
  

  /**
   * render section based on form type
   * @param formType 
   * @param animalType 
   * @param animalIndex 
   * @returns 
   */
  const renderSection = useCallback((formType: string, animalType?: string, animalIndex?: number) => {
    const rawAgentFormTemplate = (agentFomTemplate as FormJsonSection[])?.find((template) => template.type === formType);
    if (rawAgentFormTemplate && hasFieldsList(rawAgentFormTemplate as FormTemplateItem)) {
      return renderTemplates(
        rawAgentFormTemplate.fieldsList,
        { register, getValues, watch, handleAddCard, handleUploadPetDocuments, handleBulkApproveAll, unregister, handleAgentDecisionTypeChange },
        animalIndex,
        animalType
      );
    }
    return null;
  }, [agentFomTemplate, register, getValues, watch, handleAddCard, handleUploadPetDocuments, handleBulkApproveAll, unregister]);

  


  /**
   * Form submission handler
   * @param data - Form data collected from react-hook-form
   */
  // Pass saveType as param
  const onSubmit = (saveType: string): SubmitHandler<FormValues> => async (data) => {
    let requestPayload = {};
    const effectiveDecisionType = isQldRegion() ? "i_will_complete_then_request_sign" : data?.agent_decision_type;
    const validItems = items.filter(item => typeof item.pet_id === "string" && item.pet_id !== null && item.pet_id !== undefined);
    const petsPayload = validItems.map(item => ({
      pet_id: item.pet_id as string,
      pet_type: item.type,
      pet_index: item.uid,
      fields: getPetInputByUuid(data, validItems as { pet_id: string; type: string; uid: number; }[], item.pet_id as string)
    }));
    requestPayload = {
      landlords_first_name: data?.landlords_first_name,
      landlords_lastname: data?.landlords_last_name,
      landlords_email: data?.landlords_email,
      landlords_phone: data?.landlords_phone,
      general_consent: effectiveDecisionType === "i_will_complete_then_request_sign" && data?.general_consent,
      conditions_tenants: effectiveDecisionType === "i_will_complete_then_request_sign" && data?.conditions_tenants,
      pets: effectiveDecisionType === "i_will_complete_then_request_sign" && petsPayload,
      application_type: saveType,
      save_type: saveType,
      from: "agent",
      email_notification: data?.email_notification,
      sms_notification: data?.sms_notification,
      agent_decision_type: effectiveDecisionType,
    };

    secureLocalStorage.setItem('agent_decision_type', effectiveDecisionType);

    try {
      showLoader();
      const response = await dispatch(submitTenantResponseThunk({ agentId, applicationId: agentLink, agentResponseData: requestPayload as SubmitAgentRequestPayload }));
      
      if(isFulfilled(response)) {
        secureLocalStorage.setItem("landlord_signing_link", (response?.payload as any)?.data?.data?.landlord_signing_link || "");
        if (saveType === "send_email") {
          window.location.replace(AGENT_SUCCESS_PATH); // replace() instead of href: removes the submitted form from history so Back can't return to it (or restore it from bfcache)
        } else if (saveType === "save") {
          if(deviceType !== "mobile") {
            setActiveStage(1);
            setShowSaveSuccessModal(true);
          } else{
            mobileActiveStage !== 1 && setShowSaveSuccessModal(true);
            setMobileActiveStage(2);
          }                
        }
      } else {
        if(saveType === "save" && deviceType === "mobile"){
          mobileActiveStage !== 1 && setShowSaveSuccessModal(true);
          setMobileActiveStage(2);
        }
        await dispatch(setError("Failed to submit the form. Please try again."));
      }
    } catch (error) {
      // API error occurred
    } finally{
      // Scroll to top on stage change - only for error cases
      // Success cases handle scroll before state changes      
      window.scrollTo({ top: 0, behavior: 'auto' });
      hideLoader();
    }
  };

  const handleViewProfile = (
      type: string,
      idx: number,
      petData: PetDataFormat | TenantPetsData | null,
      petDocuments: TenantPetsDocument[]
    ) => {
   setSelectedPetData(petData);
    setSelectedPetDocuments(petDocuments);
    setSelectedType(type);
    setSelectedIdx(idx);
    // Reset modal form with selected pet data
    modalForm.reset(petData || {});
    setShowProfileModal(true);
  };

  const handleAgentDecisionModalClose = () => {
    setShowAgentDecisionModal(false);
    setValue("agent_decision_type", "i_will_complete_then_request_sign");
    setAgentDecisionType("i_will_complete_then_request_sign");
  }

  const handleRequestToComplete = () => {
    setShowAgentDecisionModal(false);
    setValue("agent_decision_type", "send_to_landlord_to_complete");
    setAgentDecisionType("send_to_landlord_to_complete");
  }

  /**
   * Handles agent decision type change event from input element.
   * @param event - ChangeEvent from input element
   */
  const handleAgentDecisionTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const decisionType = event.target.value;
    setAgentDecisionType(decisionType); 
    if(decisionType === "send_to_landlord_to_complete") {
      setShowAgentDecisionModal(true);
    }
  }


  // Render starts here
  return (
    <>
    {error && <ErrorAlert message={error} />}
    {
      !error && (
      <TenantLayout roleType="agent" activeStage={activeStage}>
        <FormProvider {...methods}>
          <form
            onKeyDown={(e) => {
              // Prevent Enter key submission except in textarea elements
              if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA') {
                e.preventDefault();
              }
            }}
          >
            <div className={`d-flex flex-column gap-40px pb-${bottomBarHeight}px pb-md-0`}>
              <div className="w-100 max-w-960px px-30px px-lg-0 mx-auto d-flex flex-column gap-40px">


                {/* Application summary section as reusable component */}
                {
                  mobileActiveStage === 1 || deviceType !== "mobile" ? ( 
                      <ApplicationSummary
                        applicationReceivedOn={applicationReceivedOn}
                        primaryTenants={primaryTenants ?? []}
                        tenantPropertyAddress={tenantPropertyAddress}
                        formatTenantList={formatTenantList}
                        daysLeft={dayLeftToAutoComplete}
                        approvalText="automatic approval without conditions"
                      />
                  ) : null}

                {activeStage === 2 && (
                  <AgentLandLord
                    stageOnePetsData={stageOnePetsData ?? []}
                    stageOnePetsDocumentData={stageOnePetsDocumentData ?? []}
                    activeStage={deviceType === "mobile" ? mobileActiveStage : activeStage}
                    items={items}
                    agentFomTemplate={agentFomTemplate}
                    groupedItems={groupedItems}
                    handleAddCard={handleAddCard}
                    handleUploadPetDocuments={handleUploadPetDocuments}
                    renderSection={renderSection}
                    renderTemplates={renderTemplates}
                    register={register}
                    handleBulkApproveAll={handleBulkApproveAll}
                    handleViewProfile={handleViewProfile}
                    landLordStage={landLordStage}
                    setLandLordStage={setLandLordStage}
                    handleAgentDecisionTypeChange={handleAgentDecisionTypeChange}
                    agentDecisionType={agentDecisionType}
                    isQld={isQldRegion()}
                  />
                )}

                {activeStage === 1 && (                  
                  <div className="d-flex flex-column gap-20px">
                    <div className="fs-18 fw-bold text-black">Profiles for the pets the tenant has and/or intends to keep:</div>
                    {items?.length > 0 && (
                      <div className="d-flex flex-column gap-20px text-black">
                        {typeOrder.map((type) =>
                          groupedItems?.[type]?.map((item, idx) => {
                            // Find pet data for this type and index
                            const petData = stageOnePetsData?.find(
                              (pet) =>
                                pet.id === item.pet_id
                            );
                            // Find document data for this pet (customize as needed)
                            const supportDocuments = stageOnePetsData?.find(
                              (petDoc) => petDoc.id === item.pet_id
                            )?.support_documents;

                            const profileTitle = getProfileTitle(type, idx);

                            return (
                              <div
                                key={item.uid + item.type}
                                ref={(el) => { profileRefs.current[`${type}-${item.uid}`] = el; }}
                              >
                                <AnimalProfile
                                type={item.type}
                                title={profileTitle}
                                onRemove={() => {}}
                                animalType={type}
                                petData={petData ? [petData] : undefined}
                                petDocumentData={
                                  supportDocuments ? supportDocuments : undefined
                                }
                                roleType="agent"
                                activeState={activeStage}
                                handleViewProfile={handleViewProfile}
                                collapse={openProfileKey !== `${type}-${item.uid}`}
                                onOpen={() => {
                                  const key = `${type}-${item.uid}`;
                                  const isCurrentlyOpen = openProfileKey === key;

                                  // Update state to trigger collapse/expand
                                  if (activeStage === 1) {
                                    setOpenProfileKey((prev) => (prev === key ? null : key));
                                  } else {
                                    setOpenProfileKey(key);
                                  }

                                  // Only adjust scroll when opening (not closing)
                                  if (!isCurrentlyOpen) {
                                    // Wait for DOM to update, then scroll to show the profile header
                                    requestAnimationFrame(() => {
                                      requestAnimationFrame(() => {
                                        const element = profileRefs.current[key];
                                        if (element) {
                                          element.scrollIntoView({
                                            behavior: 'auto',
                                            block: 'start'
                                          });
                                        }
                                      });
                                    });
                                  }
                                }}
                                // collapse={collapseStates[`${type}-${item.uid}`] || false}
                                // onOpen={() => setCollapseStates(prev => ({ ...prev, [`${type}-${item.uid}`]: !(prev[`${type}-${item.uid}`] || false) }))}
                              >
                                {formTemplate &&
                                  formTemplate.length > 0 &&
                                  renderAnimalCards(type, idx)}
                                <div className="d-flex gap-20px flex-column">
                                  { supportDocuments && supportDocuments.length > 0 &&
                                    <div className="fs-16px fw-normal text-black">
                                      <strong>{isQldRegion() ? "9" : type === "fish" ? "3" : "4"}. Supporting Documents</strong>
                                    </div>
                                  }
                                  {supportDocuments &&
                                    supportDocuments.length > 0 && supportDocuments.map((document, idKey) => {
                                      return (
                                        <SupportDocument key={idKey} item={document} petData={petData}/>
                                      )
                                    })}
                                  </div>
                              </AnimalProfile>
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div ref={bottomBarRef} className="d-flex flex-column flex-md-row gap-20px justify-content-md-end position-xs-fixed bottom-0 start-0 end-0 bg-white p-30px p-md-0 drop-shadow-xs-2">
                  {
                    mobileActiveStage === 1 && activeStage === 2 && deviceType === "mobile"  && agentDecisionType === "i_will_complete_then_request_sign" && (
                      <Button
                        iconName={"arrowRight"}
                        iconPosition={"right"}
                        text={"Next Step"}
                        type="button"
                        onClick={() => {
                          setMobileActiveStage(2);
                          window.scrollTo(0, 0);
                        }}
                        disabled={!isValid}
                      />
                    )
                  }
                  {
                    mobileActiveStage === 2 && deviceType === "mobile" && agentDecisionType === "i_will_complete_then_request_sign" && (
                      <Button
                        iconName={"arrowLeft"}
                        iconPosition={"left"}
                        text={"Previous Step"}
                        type="button"
                        onClick={() => {
                          setMobileActiveStage(1);
                          window.scrollTo(0, 0);
                        }}
                      />
                    )
                  }
                  {activeStage === 1 && (
                    <Button
                      iconName={"arrowRight"}
                      iconPosition={"right"}
                      text={"Continue to Response"}
                      onClick={() => {
                        setActiveStage(2);
                        // QLD has no stage-1 content in AgentLandLord (decision type + landlord sections removed),
                        // so skip mobileActiveStage 1 and go directly to stage 2 on mobile.
                        if (isQldRegion() && deviceType === "mobile") {
                          setMobileActiveStage(2);
                        }
                        window.scrollTo(0, 0);
                      }}
                    />
                  )}
                  {activeStage === 2 && (
                      <Button
                        iconName={isQldRegion() || agentDecisionType === "i_will_complete_then_request_sign" ? "arrowRight" : "email"}
                        iconPosition={"left"}
                        text={isQldRegion() || agentDecisionType === "i_will_complete_then_request_sign" ? "Submit" : "Send to Landlord"}
                        disabled={!isValid || (deviceType === "mobile" && mobileActiveStage === 1 && agentDecisionType === "i_will_complete_then_request_sign")}
                        type="button"
                        onClick={() => {
                          handleSubmit(onSubmit('send_email'))();
                        }}
                      />
                  )}
                 {error && <ErrorAlert message={error} />}
                </div>
              </div>
            </div>
          </form>
        </FormProvider>
      </TenantLayout>
      )
    }


    {showProfileModal && selectedPetData && (
      <ProfileModal
        open={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        modalForm={modalForm}
        selectedType={selectedType}
        selectedIdx={selectedIdx}
        selectedPetData={selectedPetData}
        selectedPetDocuments={selectedPetDocuments}
        formTemplate={formTemplate}
        renderAnimalCards={renderAnimalCards}
        deviceType={deviceType}
      />
    )}


    {showAgentDecisionModal === true && (
      <Modal onClose={handleAgentDecisionModalClose} width="650">
        <div className="d-flex flex-column gap-15px p-30px text-black">
          <div className="fs-18px fw-bold">
            Changing this setting will clear the landlord's response
          </div>
          <div className="fs-16px">
           Any fields you may have filled in for the landlord's response will be removed, if you choose &quot;Send to the landlord for them to complete{isQldRegion() ? "" : " and sign"}&quot;.
          </div>
          <div className="d-flex flex-column flex-md-row gap-20px justify-content-md-end">            
            <Button
              type="button"
              text="Continue on their behalf"
              className="btn-theme bg-grey-4 text-black"
              displayIcon={false}
              onClick={ () => { handleAgentDecisionModalClose();}}
            />
            <Button
              type="button"
              text="Request landlord to complete"
              className="btn-theme bg-grey-1 text-white"
              onClick={handleRequestToComplete}
              displayIcon={false}
            />
          </div>
        </div>
      </Modal>
    )}
    </>
  );
};

export default Agent;
